import { useState } from "react";

import axios from "axios";
import { FileArrowDown, Warning } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

export function ImportDialog({ open, onOpenChange, sessionId, onSuccess, API }) {
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setConfirmStep(true);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setConfirmStep(true);
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setImporting(true);
      const response = await axios.post(
        `${API}/data/import?session_id=${sessionId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      handleClose();
      onSuccess();
    } catch (err) {
      
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setConfirmStep(false);
    onOpenChange(false);
  };

  const handleBack = () => {
    setSelectedFile(null);
    setConfirmStep(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase tracking-tight">
            Gesamttabelle importieren
          </DialogTitle>
          <DialogDescription>
            {confirmStep 
              ? "Bestätigen Sie den Import der CSV-Datei."
              : "Laden Sie eine exportierte Gesamttabelle hoch."
            }
          </DialogDescription>
        </DialogHeader>

        {!confirmStep ? (
          <div className="space-y-4 pt-4">
            {/* Warning notice */}
            <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-amber-700 dark:text-amber-400">
              <Warning className="h-5 w-5 flex-shrink-0 mt-0.5" weight="fill" />
              <p className="text-sm">
                <strong>Achtung:</strong> Beim Import wird die bestehende Tabelle vollständig ersetzt!
              </p>
            </div>

            {/* Drop zone */}
            <div
              className={`upload-zone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="import-file-upload"
                data-testid="import-file-input"
              />
              <label htmlFor="import-file-upload" className="cursor-pointer">
                <FileArrowDown className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">
                  CSV-Datei hierher ziehen
                </p>
                <p className="text-xs text-muted-foreground">
                  oder <span className="text-primary underline">durchsuchen</span>
                </p>
              </label>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Unterstützt werden CSV-Dateien im Export-Format dieser App.
            </p>
          </div>
        ) : (
          <div className="space-y-4 pt-4">
            {/* Confirmation step */}
            <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded text-destructive">
              <Warning className="h-6 w-6 flex-shrink-0" weight="fill" />
              <div>
                <p className="font-semibold mb-1">Warnung: Daten werden überschrieben!</p>
                <p className="text-sm opacity-90">
                  Die gesamte bestehende Tabelle wird durch die importierte Datei ersetzt. 
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>

            <div className="p-3 bg-secondary rounded">
              <p className="text-sm">
                <span className="text-muted-foreground">Ausgewählte Datei:</span>
                <br />
                <strong>{selectedFile?.name}</strong>
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={importing}
                className="flex-1"
              >
                Zurück
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmImport}
                disabled={importing}
                className="flex-1"
                data-testid="confirm-import-button"
              >
                {importing ? "Importieren..." : "Importieren & Ersetzen"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
